import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import DialogTitle, { type DialogTitleProps } from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import React, { type MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

type Props = DialogTitleProps & {
  title: string;
  subTitle?: string;
  onClose?: MouseEventHandler<HTMLButtonElement>;
  height?: string;
  underline?: boolean;
  actions?: React.ReactNode;
};

const Header: React.FC<Props> = ({
  title,
  subTitle,
  onClose,
  height = '56px',
  underline = false,
  actions,
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const defaultTitleStyles = {
    color: theme.palette.text.primary,
    fontFamily: 'Roboto',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 500,
    letterSpacing: '0.15px',
    fontFeatureSettings: "'liga' off, 'clig' off"
  };

  const alternateTitleStyles = {
    color: theme.palette.text.primary,
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    letterSpacing: '0.15px',
    fontFeatureSettings: "'liga' off, 'clig' off"
  };

  const subTitleStyles = {
    color: theme.palette.text.secondary,
    fontFamily: 'Roboto',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: '0.4px',
    fontFeatureSettings: "'liga' off, 'clig' off"
  };

  const renderHeaderContent = () => {
    if (subTitle) {
      if (underline) {
        return (
          <>
            <Typography sx={alternateTitleStyles}>{title}</Typography>
            <Typography sx={subTitleStyles}>{subTitle}</Typography>
          </>
        );
      } else {
        return (
          <>
            <Typography sx={subTitleStyles}>{subTitle}</Typography>
            <Typography sx={alternateTitleStyles}>{title}</Typography>
          </>
        );
      }
    }
    return <Typography sx={defaultTitleStyles}>{title}</Typography>;
  };

  return (
    <DialogTitle
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: height
      }}
      {...props}
    >
      <Box>{renderHeaderContent()}</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {actions}
        {onClose && (
          <Tooltip title={t('close')}>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </DialogTitle>
  );
};

export default Header;
